import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { DispatchDetailDrawer } from "@/components/composer/DispatchDetailDrawer";
import { useWorkspace } from "@/hooks/useWorkspace";

import {
  loadDispatchHistory,
  type ContentConnectorType,
  type ContentDispatchStatus,
  type ContentPublishingConnector,
  type ContentPublishingDispatch,
  type DispatchHistoryRow,
} from "@/lib/composerCore";
import { AppSeo } from "@/components/AppSeo";

const PAGE_SIZE = 50;

const ComposerDispatches = () => {
  const { active } = useWorkspace();
  // Composer is universal across tiers — no tier gate here. See mem://features/composer-access-policy.md.

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<DispatchHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<"all" | ContentDispatchStatus>("all");
  const [connectorType, setConnectorType] = useState<"all" | ContentConnectorType>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [drawerDispatch, setDrawerDispatch] = useState<DispatchHistoryRow | null>(null);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    void loadDispatchHistory(active.id, {
      status,
      connectorType,
      search,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })
      .then((result) => {
        if (cancelled) return;
        setRows(result.rows);
        setTotal(result.total);
      })
      .catch((err) => {
        if (!cancelled) toast.error((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, status, connectorType, search, page]);

  const drawerConnector = useMemo<ContentPublishingConnector | null>(() => {
    if (!drawerDispatch?.connector) return null;
    return {
      id: drawerDispatch.connector.id,
      workspace_id: drawerDispatch.workspace_id,
      connector_type: drawerDispatch.connector.connector_type,
      display_name: drawerDispatch.connector.display_name,
      status: drawerDispatch.connector.status,
      config: {},
      created_by_user_id: drawerDispatch.requested_by_user_id,
      last_used_at: null,
      last_error_message: null,
      created_at: drawerDispatch.created_at,
      updated_at: drawerDispatch.updated_at,
    };
  }, [drawerDispatch]);

  if (!active) return null;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
    <AppSeo title="Dispatch history" />
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/composer">
              <ArrowLeft className="h-4 w-4" />
              Back to Composer
            </Link>
          </Button>
          <h1 className="font-serif text-2xl font-semibold">Dispatch history</h1>
          <p className="text-sm text-muted-foreground">
            Every send attempt across all publications and connectors in this workspace.
          </p>
        </div>
      </header>

      <Card className="shadow-soft">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Status</label>
            <Select value={status} onValueChange={(v) => { setPage(0); setStatus(v as typeof status); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="succeeded">Succeeded</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Connector type</label>
            <Select value={connectorType} onValueChange={(v) => { setPage(0); setConnectorType(v as typeof connectorType); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="webhook">Webhook</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="kit">Kit</SelectItem>
                <SelectItem value="bento">Bento</SelectItem>
                <SelectItem value="wordpress">WordPress.com</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label className="text-xs text-muted-foreground">Search (brief, publication, connector, error)</label>
            <Input
              value={search}
              onChange={(e) => { setPage(0); setSearch(e.target.value); }}
              placeholder="Type to filter…"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-soft">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {loading ? "Loading…" : `${total} dispatch${total === 1 ? "" : "es"}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No dispatches match the current filters.</p>
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setDrawerDispatch(row)}
                    className="flex w-full flex-wrap items-center gap-3 px-1 py-3 text-left transition-colors hover:bg-muted/50"
                  >
                    <Badge
                      variant={row.status === "succeeded" ? "secondary" : row.status === "failed" ? "destructive" : "outline"}
                      className="capitalize"
                    >
                      {row.status}
                    </Badge>
                    <Badge variant="outline" className="capitalize">
                      {row.connector?.connector_type ?? "—"}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {row.publication?.prepared_title || row.brief?.title || "Untitled publication"}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {row.connector?.display_name ?? "Removed connector"}
                        {row.brief?.title ? ` · ${row.brief.title}` : ""}
                        {row.error_message ? ` · ${row.error_message}` : ""}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(row.created_at).toLocaleString()}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-between">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                Previous
              </Button>
              <p className="text-xs text-muted-foreground">
                Page {page + 1} of {totalPages}
              </p>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <DispatchDetailDrawer
        open={Boolean(drawerDispatch)}
        onOpenChange={(open) => { if (!open) setDrawerDispatch(null); }}
        dispatch={drawerDispatch as ContentPublishingDispatch | null}
        connector={drawerConnector}
        publicationTitle={drawerDispatch?.publication?.prepared_title ?? null}
      />
    </div>
    </>
  );
};

export default ComposerDispatches;
